import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api";
import { toast } from "sonner";
import { LogIn, LogOut, Coffee, Play, Clock, CheckCircle2, AlertTriangle, Zap, Fingerprint } from "lucide-react";

const timeOf = (iso) => { try { return new Intl.DateTimeFormat("it-IT", { hour: "2-digit", minute: "2-digit", hour12: false, timeZone: "Europe/Rome" }).format(new Date(iso)); } catch { return "--:--"; } };
const fmtHM = (min) => {
  if (min == null) return "--";
  const m = Math.max(0, Math.round(min));
  return `${Math.floor(m / 60)}h${m % 60 > 0 ? ` ${m % 60}m` : ""}`;
};

const fmtDur = (ms) => {
  const s = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const pad = (n) => String(n).padStart(2, "0");
  return h > 0 ? `${h}:${pad(m)}:${pad(sec)}` : `${m}:${pad(sec)}`;
};

const STATUS = {
  presente: { cls: "bg-green-100 text-green-700 border-green-200", label: "Presente", icon: CheckCircle2 },
  permesso: { cls: "bg-amber-100 text-amber-800 border-amber-300", label: "Permesso", icon: AlertTriangle },
  straordinario: { cls: "bg-indigo-100 text-indigo-700 border-indigo-200", label: "Straordinario", icon: Zap },
};

export const QuickClock = () => {
  const navigate = useNavigate();
  const [today, setToday] = useState(null);
  const [busy, setBusy] = useState(false);
  const [nowTs, setNowTs] = useState(Date.now());

  const load = () => api.get("/attendance/today").then((r) => setToday(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const hasIn = today?.check_in;
  const hasOut = today?.check_out;
  const pauses = today?.pauses || [];
  const openPause = pauses.length > 0 && !pauses[pauses.length - 1].end;
  const badge = hasOut && today?.status ? STATUS[today.status] : null;

  useEffect(() => {
    if (!openPause) return;
    setNowTs(Date.now());
    const id = setInterval(() => setNowTs(Date.now()), 1000);
    return () => clearInterval(id);
  }, [openPause]);

  const pauseStart = openPause ? pauses[pauses.length - 1].start : null;
  const pauseElapsed = pauseStart ? nowTs - new Date(pauseStart).getTime() : 0;

  const act = async (path, okMsg) => {
    setBusy(true);
    try { await api.post(`/attendance/${path}`); toast.success(okMsg); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="quick-clock-card" className="mb-7 rounded-xl bg-gradient-to-br from-[#16345c] to-[#0d1b2f] text-white p-5 sm:p-6 shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <h2 className="flex items-center gap-2 text-[15px] font-black uppercase tracking-tight"><Fingerprint size={18} /> Timbratura rapida</h2>
        <button onClick={() => navigate("/presenze")} data-testid="quick-clock-open" className="text-[12px] font-semibold text-white/70 hover:text-white transition-colors">Apri Presenze</button>
      </div>

      <div className="flex items-center justify-center gap-6 mb-4">
        <div className="text-center">
          <div className="text-[11px] text-gray-300">Entrata</div>
          <div className="text-[24px] font-black" data-testid="quick-clock-checkin">{hasIn ? timeOf(today.check_in) : "--:--"}</div>
        </div>
        <div className="w-px h-10 bg-white/20" />
        <div className="text-center">
          <div className="text-[11px] text-gray-300">Uscita</div>
          <div className="text-[24px] font-black" data-testid="quick-clock-checkout">{hasOut ? timeOf(today.check_out) : "--:--"}</div>
        </div>
      </div>

      {!hasIn && (
        <button onClick={() => act("checkin", "Entrata registrata")} disabled={busy} data-testid="quick-checkin"
          className="w-full flex items-center justify-center gap-2 bg-[#009640] hover:bg-[#007a34] text-white font-black text-[16px] py-4 rounded-lg transition-colors disabled:opacity-60">
          <LogIn size={20} /> Timbra Entrata
        </button>
      )}

      {hasIn && !hasOut && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {openPause && (
            <div data-testid="quick-pause-timer" className="sm:col-span-2 flex items-center justify-center gap-2 bg-amber-400/20 border border-amber-300/40 text-amber-100 font-bold text-[15px] py-2.5 rounded-lg">
              <span className="w-2 h-2 rounded-full bg-amber-300 animate-pulse" />
              <Coffee size={16} /> In pausa da <span className="tabular-nums">{fmtDur(pauseElapsed)}</span>
            </div>
          )}
          {openPause ? (
            <button onClick={() => act("pause/end", "Pausa terminata")} disabled={busy} data-testid="quick-pause-end"
              className="flex items-center justify-center gap-2 bg-[#f0ad4e] hover:bg-[#e09b3d] text-white font-bold text-[15px] py-4 rounded-lg transition-colors disabled:opacity-60">
              <Play size={18} /> Fine Pausa
            </button>
          ) : (
            <button onClick={() => act("pause/start", "Pausa iniziata")} disabled={busy} data-testid="quick-pause-start"
              className="flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white font-bold text-[15px] py-4 rounded-lg transition-colors disabled:opacity-60">
              <Coffee size={18} /> Inizio Pausa
            </button>
          )}
          <button onClick={() => act("checkout", "Uscita registrata")} disabled={busy || openPause} data-testid="quick-checkout"
            className="flex items-center justify-center gap-2 bg-[#e30613] hover:bg-[#c00510] text-white font-black text-[16px] py-4 rounded-lg transition-colors disabled:opacity-60">
            <LogOut size={20} /> Timbra Uscita
          </button>
          {openPause && <div className="sm:col-span-2 text-[11px] text-amber-200">Termina la pausa in corso per poter timbrare l'uscita.</div>}
        </div>
      )}

      {hasOut && badge && (
        <div className="space-y-2" data-testid="quick-status">
          <div className="flex items-center justify-center gap-2 bg-white/10 text-white font-bold text-[14px] py-2.5 rounded-lg"><Clock size={16} /> Prestazione netta: {fmtHM(today.net_minutes)}</div>
          <div className={`flex items-center justify-center gap-2 border font-bold text-[13.5px] py-2.5 rounded-lg ${badge.cls}`}>
            <badge.icon size={16} /> {badge.label}
            {today.status === "permesso" && ` di ${fmtHM(today.permesso_minutes)}`}
            {today.status === "straordinario" && ` di ${fmtHM(today.straordinario_minutes)}`}
          </div>
        </div>
      )}
    </div>
  );
};
